import { useState } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { duckTitle, MAX_NAME, patchGalleryCache } from "../lib/api";
import { renameDuck } from "../lib/renameDuck";

// The "<name> is sculpted" heading with a pencil that turns it into an inline rename field.
export const RenameTitle = ({ creation, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const title = duckTitle(creation);

  const start = () => { setName(title); setError(""); setEditing(true); };

  const save = async () => {
    if (!name.trim()) return setError("Give your duck a name");
    setBusy(true);
    try {
      const res = await renameDuck(creation.id, name);
      setEditing(false);
      if (!res) return; // "Cancel rename": nothing changed
      patchGalleryCache(creation.id, { name: res.data.name });
      onUpdate?.(res.data);
      toast.success("Duck renamed");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not rename the duck");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <form data-testid="rename-form" className="space-y-1" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <div className="flex items-center gap-2">
          <input
            autoFocus
            data-testid="rename-input"
            value={name}
            maxLength={MAX_NAME}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setEditing(false)}
            className="flex-1 min-w-0 rounded-xl border border-border bg-background px-3 py-2 font-heading text-xl sm:text-2xl font-bold tracking-tight focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <button type="submit" data-testid="rename-save-btn" disabled={busy} className="rounded-full bg-accent text-accent-foreground p-2 disabled:opacity-50" title="Save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </button>
          <button type="button" data-testid="rename-cancel-btn" disabled={busy} onClick={() => setEditing(false)} className="rounded-full border border-border p-2" title="Cancel">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[11px] font-mono2 text-muted-foreground" data-testid="rename-count">{name.length}/{MAX_NAME}{error && <span data-testid="rename-error" className="ml-2 text-destructive">{error}</span>}</p>
      </form>
    );
  }
  return (
    <h2 data-testid="ready-title" className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-start gap-2">
      <span>{title ? `${title} is sculpted 🎉` : "Your figurine is sculpted 🎉"}</span>
      {onUpdate && (
        <button type="button" data-testid="rename-btn" onClick={start} className="mt-1.5 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Rename this duck">
          <Pencil className="h-4 w-4" />
        </button>
      )}
    </h2>
  );
};
